import { cloneDeep } from 'lodash';
import { useMemo, useState } from 'react';

export const newEmptySheet = {
  id: 0,
  source: {
    id: 0,
    name: 'Select Source'
  },
  name: 'Select Sheet'
};

export const normalizeAutomationFundMappings = fundMappings => {
  function getMappedFund(fund, isNotMappedFund = false) {
    return [
      ...fund.mapped_bulk_upload_files?.mapped_list?.map(bulkFile => {
        const prevFile = bulkFile.previous_period_file || bulkFile;
        const currFile = bulkFile.current_period_file || bulkFile;

        const mappedSheets = bulkFile.sheet_mappings.mapped_list.map(sheet => ({
          ...sheet.current_period_sheet,
          source: cloneDeep(sheet.previous_period_sheet)
        }));

        const manualExtractedSheets = (
          bulkFile?.current_period_file?.sheets_for_manual_extraction || []
        ).map(sheet => ({
          ...sheet,
          source: {
            id: 1,
            name: 'Manual Entry'
          }
        }));

        const mappedSheetIds = new Set(
          bulkFile.sheet_mappings.mapped_list.map(sheet => sheet.current_period_sheet.id)
        );

        const notMappedSheets = bulkFile.sheet_mappings.no_match_current_period_list.filter(
          sheet => !mappedSheetIds.has(sheet.id)
        );

        const allSheetIds = new Set([
          ...(prevFile.sheets?.map(sheet => sheet.id) || []),
          ...mappedSheets.map(sheet => sheet.source.id)
        ]);

        const sortedMappedList = Array.from(allSheetIds).map(id => {
          const prevSheet = prevFile.sheets?.find(sheet => sheet.id === id);
          const mappedSheet = mappedSheets.find(sheet => sheet.source.id === id);

          return {
            ...(mappedSheet || newEmptySheet),
            source: cloneDeep(prevSheet || mappedSheet?.source)
          };
        });

        return {
          isMapped: !isNotMappedFund,
          isPerFund: false,
          previousFile: { ...prevFile, isMapped: !isNotMappedFund },
          currentFile: {
            ...currFile,
            sheets: [...(sortedMappedList || currFile.sheets), ...manualExtractedSheets],
            notMappedSheets,
            isMapped: !isNotMappedFund
          }
        };
      })
    ];
  }

  function getUnmappedFund(fund) {
    return [
      ...fund.mapped_bulk_upload_files?.no_match_previous_period_list?.map(bulkFile => {
        const noMatchPreviousFile = bulkFile;

        return {
          isPerFund: false,
          previousFile: noMatchPreviousFile ? { ...noMatchPreviousFile, isMapped: false } : null,
          currentFile: null
        };
      }),
      ...fund.mapped_bulk_upload_files?.no_match_current_period_list?.map(bulkFile => {
        const sheets = bulkFile.independent_sheet_mappings.map(_sheet => ({
          ..._sheet.current_period_sheet,
          source: { ..._sheet.previous_period_sheet }
        }));

        const manualExtractedSheets = (bulkFile.sheets_for_manual_extraction || []).map(sheet => ({
          ...sheet,
          source: {
            id: 1,
            name: 'Manual Entry'
          }
        }));

        // sheets_for_manual_extraction

        return {
          isPerFund: false,
          previousFile: null,
          currentFile: {
            ...bulkFile,
            sheets: [...sheets, ...manualExtractedSheets],
            notMappedSheets: [...bulkFile.sheets],
            isMapped: false
          }
        };
      })
    ];
  }

  const mappedFunds = fundMappings.mapped_input_funds.map(mappedFund => {
    const bulkUploadedMappedFiles = [...getMappedFund(mappedFund)];

    const bulkUploadedUnMappedFiles = [...getUnmappedFund(mappedFund)];

    const individualUploadedMappedFiles = [
      ...mappedFund.mapped_per_input_files?.map(mappedFile => {
        const prevFile = mappedFile.previous_period_file || mappedFile;
        const currFile = mappedFile.current_period_file || mappedFile;

        const manualExtractedSheets = (
          mappedFile?.current_period_file?.sheets_for_manual_extraction || []
        ).map(sheet => ({
          ...sheet,
          source: {
            id: 1,
            name: 'Manual Entry'
          }
        }));

        const mappedSheets = mappedFile.sheet_mappings.mapped_list.map(sheet => ({
          ...sheet.current_period_sheet,
          source: cloneDeep(sheet.previous_period_sheet)
        }));

        const mappedSheetIds = new Set(
          mappedFile.sheet_mappings.mapped_list.map(sheet => sheet.current_period_sheet.id)
        );

        const notMappedSheets = mappedFile.sheet_mappings.no_match_current_period_list.filter(
          sheet => !mappedSheetIds.has(sheet.id)
        );

        const allSheetIds = new Set([
          ...(prevFile.sheets?.map(sheet => sheet.id) || []),
          ...mappedSheets.map(sheet => sheet.source.id)
        ]);

        const sortedMappedList = Array.from(allSheetIds).map(id => {
          const prevSheet = prevFile.sheets?.find(sheet => sheet.id === id);
          const mappedSheet = mappedSheets.find(sheet => sheet.source.id === id);

          return {
            ...(mappedSheet || newEmptySheet),
            source: cloneDeep(prevSheet || mappedSheet?.source)
          };
        });

        return {
          isMapped: true,
          isPerFund: true,
          previousFile: { ...prevFile, isMapped: true },
          currentFile: {
            ...currFile,
            sheets: [...(sortedMappedList || currFile.sheets), ...manualExtractedSheets],
            notMappedSheets,
            isMapped: true
          }
        };
      })
    ];

    const individualUploadedUnmappedFiles = [
      ...mappedFund.current_fund.not_mapped_per_input_files?.map((file, index) => {
        const mappedSheets =
          file.independent_sheet_mappings?.map(sheet => ({
            ...sheet?.current_period_sheet,
            source: sheet?.previous_period_sheet
          })) || [];

        const manualExtractedSheets = (file?.sheets_for_manual_extraction || []).map(sheet => ({
          ...sheet,
          source: {
            id: 1,
            name: 'Manual Entry'
          }
        }));

        const filteredSheets = file.sheets.reduce((arr, curr) => {
          if (!mappedSheets.find(sheet => sheet.id === curr.id)) {
            return [...arr, curr];
          }

          return arr;
        }, []);

        return {
          isPerFund: true,
          previousFile: null,
          currentFile: {
            ...file,
            sheets: [...mappedSheets, ...manualExtractedSheets],
            notMappedSheets: [...filteredSheets],
            isMapped: false
          }
        };
      })
    ];

    const prevIndividualFiles = [
      ...mappedFund.previous_fund?.not_mapped_per_input_files?.map(file => {
        return {
          isPerFund: true,
          isMapped: false,
          previousFile: { ...(file || {}), isMapped: false },
          currentFile: null
        };
      })
    ];

    const perFundFiles = [
      ...individualUploadedMappedFiles,
      ...prevIndividualFiles,
      ...individualUploadedUnmappedFiles
    ];
    const filteredPerFundFiles = [];
    const seenIds = new Set();

    perFundFiles.forEach(item => {
      const id = item.previousFile?.id;
      if (id && !seenIds.has(id)) {
        seenIds.add(id);
        filteredPerFundFiles.push(item);
      }
    });

    return {
      currentFund: mappedFund.current_fund,
      previousFund: mappedFund.previous_fund,
      bulkFiles: [...bulkUploadedMappedFiles, ...bulkUploadedUnMappedFiles],
      perFundFiles: perFundFiles
    };
  });

  const notMappedFunds = fundMappings.not_mapped_current_input_funds.map(fund => {
    const mappedFund = fundMappings.mapped_input_funds?.[0];
    let bulkUploadedFiles = [];
    let bulkUnmappedUploadedFiles = [];

    if (mappedFund) {
      bulkUploadedFiles = [...getMappedFund(mappedFund, true)];
      bulkUnmappedUploadedFiles = [...getUnmappedFund(mappedFund)];
    } else
      bulkUploadedFiles = [
        ...fund.bulk_upload_files?.map(bulkFile => {
          const manualExtractedSheets = (bulkFile?.sheets_for_manual_extraction || []).map(
            sheet => ({
              ...sheet,
              source: {
                id: 1,
                name: 'Manual Entry'
              }
            })
          );

          const filteredSheets = bulkFile.sheets.reduce((arr, curr) => {
            if (!manualExtractedSheets.find(sheet => sheet.id === curr.id)) {
              return [...arr, curr];
            }

            return arr;
          }, []);

          return {
            isMapped: true,
            isPerFund: false,
            previousFile: {},
            currentFile: {
              ...bulkFile,
              sheets: [...(manualExtractedSheets || [])],
              notMappedSheets: [...(filteredSheets || [])],
              isMapped: false
            }
          };
        })
      ];

    const individualUploadedUnmappedFiles = [
      ...fund.not_mapped_per_input_files?.map((file, index) => {
        const mappedSheets =
          file.independent_sheet_mappings?.map(sheet => ({
            ...sheet?.current_period_sheet,
            source: sheet?.previous_period_sheet
          })) || [];

        const manualExtractedSheets = (file?.sheets_for_manual_extraction || []).map(sheet => ({
          ...sheet,
          source: {
            id: 1,
            name: 'Manual Entry'
          }
        }));

        const filteredSheets = file.sheets?.reduce((arr, curr) => {
          if (!mappedSheets.find(sheet => sheet.id === curr.id)) {
            return [...arr, curr];
          }

          return arr;
        }, []);

        return {
          isPerFund: true,
          previousFile: null,
          currentFile: {
            ...file,
            sheets: [...(mappedSheets || []), ...(manualExtractedSheets || [])],
            notMappedSheets: [...(filteredSheets || [])],
            isMapped: false
          }
        };
      })
    ];

    return {
      currentFund: fund,
      previousFund: null,
      bulkFiles: [...bulkUploadedFiles, ...bulkUnmappedUploadedFiles],
      perFundFiles: [...individualUploadedUnmappedFiles]
    };
  });

  return [...mappedFunds, ...notMappedFunds];
};

export const normalizeInitialSetupFundMappings = fundMappings => {
  return fundMappings.map(fund => {
    const currentFund = {
      id: fund.id,
      name: fund.name,
      created_at: fund.created_at,
      modified_at: fund.modified_at,
      not_mapped_per_input_files: []
    };

    const previousFund = {
      id: null,
      name: null,
      created_at: null,
      modified_at: null,
      not_mapped_per_input_files: []
    };

    const bulkFiles = (fund.bulk_uploaded_files || []).map(file => ({
      isMapped: true,
      isPerFund: false,
      previousFile: null,
      currentFile: {
        ...file,
        id: file.id,
        name: file.name,
        format: file.format,
        sheets: file.sheets || [],
        isMapped: true
      }
    }));

    const perFundFiles = (fund.files_as_per_input || []).map(file => ({
      isMapped: true,
      isPerFund: true,
      previousFile: null,
      currentFile: {
        ...file,
        id: file.id,
        name: file.name,
        format: file.format,
        size: 0,
        sheets: file.sheets || [],
        isMapped: true
      }
    }));

    return {
      currentFund,
      previousFund,
      bulkFiles,
      perFundFiles
    };
  });
};

export default function useAutomationFiles({ fundMappings, onChangeFundMappings }) {
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedFileIds, setExpandedFileIds] = useState([]);

  const isAllExpanded = useMemo(() => {
    const allElements = fundMappings
      .map((el, idx) => {
        let arr = [];

        arr = [...(el.bulkFiles.map((_, fIdx) => `${idx}_${fIdx}`) || [])];
        arr = [...arr, ...el.perFundFiles.map((_, fIdx) => `${idx}_${el.bulkFiles.length + fIdx}`)];

        return arr;
      })
      .flat();

    return expandedFileIds.length === allElements.length;
  }, [fundMappings, expandedFileIds]);

  function openPopup(msg = 'You have unsaved data. Are you sure you want to proceed?') {
    return new Promise((resolve, reject) => {
      // Simulate opening popup
      setTimeout(() => {
        const userResponse = confirm(msg);

        if (userResponse) {
          setHasChanges(false);
        }

        resolve(userResponse);
      }, 100);
    });
  }

  const handleTrackChanges = () => {
    setHasChanges(true);
  };

  const handleSelectSource = (fundId, fileId, sheetId, value) => {
    const _fundMappings = cloneDeep(fundMappings);

    _fundMappings.map(fund => {
      fund.bulkFiles.map(file => {
        if (file?.currentFile?.id === fileId) {
          file.currentFile.sheets = [
            ...file?.currentFile?.sheets.map(sheet =>
              sheet.id === sheetId
                ? {
                    ...sheet,
                    source: value || sheet.source
                  }
                : sheet
            )
          ];
        }

        return file;
      });

      fund.perFundFiles?.map(file => {
        if (file?.currentFile?.id === fileId) {
          file.currentFile.sheets = [
            ...file?.currentFile?.sheets?.map(sheet =>
              sheet.id === sheetId
                ? {
                    ...sheet,
                    source: value || sheet?.source
                  }
                : sheet
            )
          ];
        }

        return file;
      });

      return fund;
    });

    handleTrackChanges();
    onChangeFundMappings(_fundMappings);
  };

  const handleAddMappedSheet = (fundId, fileId) => {
    const _fundMappings = cloneDeep(fundMappings);

    _fundMappings.map(fund => {
      // if (fund.currentFund.id === fundId) {
      fund.bulkFiles.map(file => {
        if (file?.currentFile?.id === fileId) {
          file.currentFile.sheets.push({ ...newEmptySheet });
        }

        return file;
      });

      fund.perFundFiles?.map(file => {
        if (file?.currentFile?.id === fileId) {
          file.currentFile.sheets.push({ ...newEmptySheet });
        }

        return file;
      });
      // }

      return fund;
    });

    handleTrackChanges();
    onChangeFundMappings(_fundMappings);
  };

  const handleRemoveMappedSheet = (fundId, fileId, index) => {
    const _fundMappings = cloneDeep(fundMappings);

    _fundMappings.map(fund => {
      // if (fund.currentFund.id === fundId) {
      fund.bulkFiles.map(file => {
        if (file?.currentFile?.id === fileId) {
          if (file.currentFile.sheets && file.currentFile.sheets[index]?.id !== 0) {
            file.currentFile.notMappedSheets = [
              ...file?.currentFile?.notMappedSheets,
              file?.currentFile?.sheets[index]
            ];
          }

          file.currentFile.sheets = [
            ...file?.currentFile?.sheets?.filter((_, idx) => idx !== index)
          ];
        }

        return file;
      });

      fund.perFundFiles?.map(file => {
        if (file.currentFile?.id === fileId) {
          if (file.currentFile?.sheets?.[index].id !== 0)
            file.currentFile.notMappedSheets = [
              ...file?.currentFile?.notMappedSheets,
              file?.currentFile?.sheets[index]
            ];

          file.currentFile.sheets = [
            ...file?.currentFile?.sheets?.filter((_, idx) => idx !== index)
          ];
        }

        return file;
      });
      // }

      return fund;
    });

    handleTrackChanges();
    onChangeFundMappings(_fundMappings);
  };

  const handleDeleteSheet = (sheetId, index, fileId) => {
    const _fundMappings = cloneDeep(fundMappings);

    _fundMappings?.map(fund => {
      fund?.bulkFiles?.map(file => {
        if (sheetId && !!file.currentFile) {
          const foundSheet = file?.currentFile?.sheets?.find(sheet => sheet.id === sheetId);

          file.currentFile.notMappedSheets = [
            ...(file.currentFile?.notMappedSheets || []),
            foundSheet
          ].filter(el => el?.id);

          file.currentFile.sheets = [
            ...file?.currentFile?.sheets?.filter(sheet => sheet?.id !== sheetId)
          ];
        }

        if (!sheetId && (index || typeof index === 'number') && file.currentFile?.id === fileId) {
          file.currentFile.notMappedSheets = [
            ...file?.currentFile?.notMappedSheets,
            file?.currentFile?.sheets?.find((_, idx) => idx === index)
          ]?.filter(el => el.id);

          file.currentFile.sheets = [
            ...file?.currentFile?.sheets?.filter((_, idx) => idx !== index)
          ];
        }

        return file;
      });

      fund?.perFundFiles?.map(file => {
        if (sheetId && file.currentFile) {
          file.currentFile.notMappedSheets = [
            ...file?.currentFile?.notMappedSheets,
            file?.currentFile?.sheets?.find(sheet => sheet.id === sheetId)
          ];

          file.currentFile.sheets = [
            ...file?.currentFile?.sheets?.filter(sheet => sheet.id !== sheetId)
          ];
        }

        if (index && file.currentFile?.id === fileId) {
          file.currentFile.notMappedSheets = [
            ...file?.currentFile?.notMappedSheets,
            file.currentFile?.sheets?.find((_, idx) => idx === index)
          ];

          file.currentFile.sheets = [
            ...file?.currentFile?.sheets?.filter((_, idx) => idx !== index)
          ];
        }

        return file;
      });

      return fund;
    });
    handleTrackChanges();
    onChangeFundMappings(_fundMappings);
  };

  const handleExpandFile = idx => {
    if (expandedFileIds.includes(idx)) setExpandedFileIds(prev => prev.filter(el => el !== idx));
    else setExpandedFileIds(prev => [...prev, idx]);
  };

  const handleExpandAll = () => {
    if (isAllExpanded) {
      setExpandedFileIds([]);
    } else {
      const allElements = fundMappings
        .map((el, idx) => {
          let arr = [];

          arr = [...(el.bulkFiles.map((_, fIdx) => `${idx}_${fIdx}`) || [])];
          arr = [
            ...arr,
            ...el.perFundFiles.map((_, fIdx) => `${idx}_${el.bulkFiles.length + fIdx}`)
          ];

          return arr;
        })
        .flat();

      setExpandedFileIds(allElements);
    }
  };

  const handleUnmapFile = (fileId, isPerFund) => {
    const _fundMappings = cloneDeep(fundMappings);

    // PER-FUND FILE → only affect one fund
    if (isPerFund) {
      let sourceFundIndex = -1;
      let foundFile = null;

      for (let i = 0; i < _fundMappings.length; i++) {
        const match = _fundMappings[i].perFundFiles?.find(f => f.currentFile?.id === fileId);

        if (match) {
          sourceFundIndex = i;
          foundFile = match;
          break;
        }
      }

      if (sourceFundIndex === -1 || !foundFile?.currentFile) return;

      const fund = _fundMappings[sourceFundIndex];

      fund.perFundFiles = fund.perFundFiles.map(file =>
        file.currentFile?.id === fileId ? { ...file, currentFile: null } : file
      );

      fund.perFundFiles.push({
        ...foundFile,
        currentFile: {
          ...foundFile.currentFile,
          notMappedSheets: [
            ...foundFile.currentFile.sheets?.map(sheet => ({ ...sheet, source: null })),
            ...foundFile.currentFile.notMappedSheets
          ],
          sheets: [],
          isMapped: false
        },
        previousFile: null,
        isMapped: false,
        isPerFund: true
      });
    }

    // BULK FILE → affect ALL funds containing this file
    else {
      let templateFile = null;

      // Find one instance just to copy structure later
      for (const fund of _fundMappings) {
        const match = fund.bulkFiles?.find(f => f.currentFile?.id === fileId);
        if (match) {
          templateFile = match;
          break;
        }
      }

      if (!templateFile?.currentFile) return;

      _fundMappings.forEach(fund => {
        const hasFile = fund.bulkFiles?.some(f => f.currentFile?.id === fileId);

        if (!hasFile) return;

        fund.bulkFiles = fund.bulkFiles.map(file =>
          file.currentFile?.id === fileId ? { ...file, currentFile: null } : file
        );

        fund.bulkFiles.push({
          ...templateFile,
          currentFile: {
            ...templateFile.currentFile,
            sheets: [],
            notMappedSheets: [
              ...templateFile.currentFile.sheets?.map(sheet => ({ ...sheet, source: null })),
              ...templateFile.currentFile.notMappedSheets
            ],
            isMapped: false
          },
          previousFile: null,
          isMapped: false
        });
      });
    }

    handleTrackChanges();
    onChangeFundMappings(_fundMappings);
  };

  const handleAutoMatchSheets = (currentFileId, previousFileId, sheetMappings) => {
    const _fundMappings = cloneDeep(fundMappings);

    _fundMappings.map(fund => {
      fund.bulkFiles.map(file => {
        if (file?.currentFile?.id === currentFileId) {
          const currentSheets = sheetMappings
            ?.filter(el => el.current_sheet)
            ?.map(el => ({
              ...el.current_sheet,
              source: { ...el.previous_sheet }
            }))
            ?.filter(el => el);

          file.currentFile.sheets = [...currentSheets];
        }

        if (file?.previousFile?.id === previousFileId) {
          file.previousFile.sheets = sheetMappings.map(el => el.previous_sheet)?.filter(el => el);
        }

        return file;
      });

      fund.perFundFiles?.map(file => {
        if (file.currentFile?.id === currentFileId) {
          const currentSheets = sheetMappings
            ?.filter(el => el.current_sheet)
            ?.map(el => ({
              ...el.current_sheet,
              source: { ...el.previous_sheet }
            }))
            ?.filter(el => el);

          file.currentFile.sheets = [...currentSheets];
        }

        if (file.previousFile?.id === previousFileId) {
          file.previousFile.sheets = sheetMappings.map(el => el.previous_sheet)?.filter(el => el);
        }

        return file;
      });

      return fund;
    });

    setHasChanges(true);
    onChangeFundMappings(_fundMappings);
  };

  const onChooseSheet = (fundId, fileId, index, value, source) => {
    const _fundMappings = cloneDeep(fundMappings);

    function assignNewValueToSheet(_file) {
      const allSheets = [
        ...(_file.currentFile?.notMappedSheets || []),
        ...(_file.currentFile?.sheets || [])
      ];

      if (_file?.currentFile?.id === fileId) {
        _file.currentFile.notMappedSheets = [
          ..._file?.currentFile?.notMappedSheets?.filter(sheet => sheet?.id !== value?.id)
        ];

        _file.currentFile.sheets = [
          ..._file?.currentFile?.sheets?.map((sheet, idx) =>
            idx === index
              ? {
                  ...value,
                  source: source ?? sheet.source
                }
              : sheet
          )
        ];

        const includedSheets = _file.currentFile.sheets.map(sheet => sheet?.id);

        _file.currentFile.notMappedSheets = [
          ...(allSheets.filter(sheet => !includedSheets.includes(sheet?.id)) || [])
        ];
      }

      return _file;
    }

    _fundMappings.map(fund => {
      fund.bulkFiles?.map(file => {
        return assignNewValueToSheet(file);
      });

      if (fund.currentFund.id === fundId) {
        fund.perFundFiles?.map(file => {
          return assignNewValueToSheet(file);
        });
      }

      return fund;
    });

    setHasChanges(true);
    onChangeFundMappings(_fundMappings);
  };

  return {
    hasChanges,
    isAllExpanded,
    expandedFileIds,
    handleSelectSource,
    handleAddMappedSheet,
    handleRemoveMappedSheet,
    handleDeleteSheet,
    handleExpandFile,
    handleExpandAll,
    onChooseSheet,
    handleTrackChanges,
    setHasChanges,
    handleUnmapFile,
    handleAutoMatchSheets,
    openPopup
  };
}
